import { useLazyQuery } from "@apollo/client";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { GridInputs, SearchForm } from "../../../../components/Functional";
import { Button, Spinner } from "../../../../components/ui";
import { FETCH_ARTISTS as fetchArtists } from "../../../../graphql/query/Artist";
import { FETCH_ARTWORKS_V2 as fetchArtworksV2Query } from "../../../../graphql/query/Artwork";
import { FETCH_ARTWORK_CATEGORIES } from "../../../../graphql/query/ArtworkCategory";
import { FETCH_ARTWORK_STANDARDS } from "../../../../graphql/query/ArtworkStandard";
import { updateSearchResultNavigationStore } from "../../../../util/searchResultNavigation";
import {
  artworkFilterStore,
  artworkNavigationStore,
  useArtworkFilters,
} from "../useArtworkFilters";
import { getArtworkListContext } from "../searchResultNavigation";
import Exhibition from "./components/Exhibition";
import Instance from "./components/Instance";
import Query from "./components/Query";
import Tabs from "./components/Tabs";
import {
  getArtworkSearchDestination,
  withExactInstanceReference,
} from "./utils";

const ArtworkSearch = props => {
  const { closeModal, isSearchOpen = false, onSearchSubmitted } = props || {};
  const [fetchArtworksV2, { loading, data: { artworksV2 } = {} }] =
    useLazyQuery(fetchArtworksV2Query());

  const [
    fetchArtistsLazily,
    { loading: artistsLoading, data: { artists = [] } = {} },
  ] = useLazyQuery(fetchArtists());

  const [
    fetchArtworkCategoriessLazily,
    {
      loading: artworkCategoriesLoading,
      data: { artworkCategories = [] } = {},
    },
  ] = useLazyQuery(FETCH_ARTWORK_CATEGORIES);

  const [
    fetchArtworkStandardssLazily,
    { loading: artworkStandardsLoading, data: { artworkStandards = [] } = {} },
  ] = useLazyQuery(FETCH_ARTWORK_STANDARDS);

  useEffect(() => {
    if (isSearchOpen) {
      fetchArtistsLazily();
      fetchArtworkCategoriessLazily();
      fetchArtworkStandardssLazily();
    }
  }, [isSearchOpen]);

  const navigate = useNavigate();
  const location = useLocation();
  const { formik } = useArtworkFilters();

  const inputs = {
    className:
      "mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-4 lg:gap-16",
    inputs: [
      {
        className: "gap-4",
        inputs: [
          {
            label: "Artist",
            name: "_artist.id",
            options: artists?.map(item => ({
              label: item.name,
              value: item.id,
            })),
            type: "multi-select",
          },
          { label: "Title", name: "title", type: "textarea" },
          {
            hasOperator: true,
            isMulti: true,
            label: "Category",
            name: "_artworkCategory.id",
            options: artworkCategories?.map(item => ({
              label: item.name,
              value: item.id,
            })),
            type: "multi-select",
          },
          {
            hasOperator: true,
            label: "Medium",
            name: "medium",
            type: "textarea",
          },
        ],
      },
      {
        className: "grid-cols-1 gap-4",
        inputs: [
          {
            label: "Year",
            name: "_edition.year",
            type: "text",
            hasOperator: true,
          },
          { label: "Series", name: "series", type: "text" },
          { label: "Dimensions", name: "dimensions", type: "textarea" },
          { label: "Description", name: "description", type: "textarea" },
        ],
      },
      {
        className: "grid-cols-1 gap-4",
        inputs: [
          { label: "Id", name: "id", type: "number" },
          { label: "Image", name: "_images.archiveId", type: "number" },
          { label: "Timestamp", name: "updatedAt", type: "text" },
          {
            label: "Handling",
            name: "standardId",
            options: artworkStandards?.map(item => ({
              label: item.name,
              value: item.id,
            })),
            type: "multi-select",
          },
        ],
      },
      {
        className: "grid-cols-1 gap-4 pt-4",
        inputs: [
          {
            designType: "threeStateCheckbox",
            label: "Photographed",
            name: "photographed",
            type: "toggle",
          },
          {
            designType: "threeStateCheckbox",
            label: "Travel Pack",
            name: "travelPack",
            type: "toggle",
          },
          {
            designType: "threeStateCheckbox",
            label: "Confidential",
            name: "confidential",
            type: "toggle",
          },
        ],
      },
    ],
  };

  const inputProps = {
    formik,
    ...inputs,
  };

  const handleSubmit = e => {
    e?.preventDefault();
    const currentParams = new URLSearchParams(location.search);
    const nextParams = new URLSearchParams();
    ["listId", "imageListId", "workingListId", "imageWorkingListId"].forEach(
      key => {
        const value = currentParams.get(key);
        if (value) {
          nextParams.set(key, value);
        }
      },
    );

    // Match the instance reference exactly, and persist it so the fallback list
    // uses the same exact filter as the count query behind the redirect.
    const searchInput = withExactInstanceReference(artworkFilterStore.get());
    artworkFilterStore.set(searchInput);

    fetchArtworksV2({
      variables: {
        input: searchInput,
      },
    }).then(result => {
      const destination = getArtworkSearchDestination({
        input: searchInput,
        edges: result?.data?.artworksV2?.edges,
        count: result?.data?.artworksV2?.count,
        listSearch: nextParams.toString(),
      });
      if (destination.startsWith("/artworks/")) {
        updateSearchResultNavigationStore(artworkNavigationStore, {
          count: result?.data?.artworksV2?.count,
          edges: result?.data?.artworksV2?.edges,
          listContext: getArtworkListContext(nextParams.toString()),
          queryVariables: { ...searchInput, offset: 0 },
          statusContext: "all",
        });
      }

      navigate(destination);
      // Performing a new search invalidates any selection from the previous
      // result set; clear it so stale ids cannot reach bulk actions.
      onSearchSubmitted?.();
      closeModal();
    });
  };

  const searchButtonProps = {
    action: "black",
    label: loading ? "Searching" : "Search",
    disabled: loading,
    type: "submit",
  };

  const resetButtonProps = {
    action: "default",
    label: "Reset",
    onClick: formik?.resetForm,
  };

  const tabComponentProps = {
    artworksV2,
    formik,
  };

  const tabsProps = {
    formik,
    name: "tab",
    options: [
      {
        label: "Instance",
        value: "instance",
        component: <Instance {...tabComponentProps} />,
      },
      {
        label: "Exhibitions",
        value: "exhibitions",
        component: <Exhibition {...tabComponentProps} />,
      },
      {
        label: "Query",
        value: "query",
        component: <Query {...tabComponentProps} />,
      },
    ],
  };

  return (
    <SearchForm onSubmit={handleSubmit}>
      <div className="flex flex-col">
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div className="text-xl font-bold">Artwork Search</div>
          <div className="flex w-full flex-wrap gap-2 sm:w-auto sm:gap-4">
            <Button {...resetButtonProps} />
            <Button {...searchButtonProps} />
          </div>
        </div>
        {artistsLoading ||
        artworkCategoriesLoading ||
        artworkStandardsLoading ? (
          <div className="flex h-[70dvh] w-full items-center justify-center ">
            <Spinner />
          </div>
        ) : (
          <div className="flex flex-col gap-4 sm:gap-6 lg:gap-8">
            <GridInputs {...inputProps} />
            <Tabs {...tabsProps} />
          </div>
        )}
      </div>
    </SearchForm>
  );
};

export default ArtworkSearch;
